import React, {useCallback, useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useAppState} from "@stores/AppStateContext";

import {Tree} from "../../components/Tree/Tree";
import {ItemContentProps} from "../../components/Tree/types";
import {DocumentItem, DocumentTreeSource} from "./DocumentTreeSource";
import {ItemLine} from '../ItemLine';
import {WorkspaceModelType, ViewType} from "@stores/WorkspaceStore";
import {InAppDeleteEventData, inAppEventsHub} from "../../../volatile/InAppEventsHub";

import {EmptyListView} from "./EmptyListView";

export interface ViewVariationProps {
    treeSource?: DocumentTreeSource,
    renderingRootId?: string,
    inEditorView?: boolean,
}

export const ViewVariation = observer((
        props: ViewVariationProps
    ) => {
        const appState = useAppState();
        const rootStore = appState.rootStore;
        const activeWorkspace = rootStore.view.workspace!;

        const {
            treeSource = appState.tree,
            inEditorView = false,
        } = props;
        const renderingRootId = props.renderingRootId
            ?? (activeWorkspace.view === 'main'
                ? activeWorkspace.selectedLibrary?.id ?? activeWorkspace.view
                : activeWorkspace.view);

        const hideDeletedItem = useCallback((data: InAppDeleteEventData) => {
            activeWorkspace.hideTreeItem(data.itemId);
        }, [activeWorkspace]);
        const cancelHideDeletedItem = useCallback((data: InAppDeleteEventData) => {
            activeWorkspace.cancelHideTreeItem(data.itemId);
        }, [activeWorkspace]);

        useEffect(() => {
            inAppEventsHub.subscribe('ItemDeleteStart', hideDeletedItem);
            inAppEventsHub.subscribe('ItemDeleteEnd', cancelHideDeletedItem);
            return () => {
                inAppEventsHub.unsubscribe('ItemDeleteStart', hideDeletedItem);
                inAppEventsHub.unsubscribe('ItemDeleteEnd', cancelHideDeletedItem);
            }
        }, [hideDeletedItem, cancelHideDeletedItem]);

        const currentTreeNode = treeSource.data.items[renderingRootId];

        const renderItemContent = useCallback(
            ({state, item, level}: ItemContentProps<DocumentItem>) => (
                <ItemLine
                    inEditorView={inEditorView}
                    item={item.data.item}
                    state={state}
                    level={level}
                    subtitle={inEditorView ? null : getStatusViewSubtitle(activeWorkspace, item, rootStore.currentLocale)}
                />
            ),
            [activeWorkspace, inEditorView, rootStore.currentLocale],
        );
        if (!currentTreeNode || currentTreeNode.childrenIds.length === 0) {
            return (
                <EmptyListView view={activeWorkspace.view as ViewType}/>
            )
        }

        return (
            <Tree
                className={!inEditorView ? '' : ''}
                treeSource={treeSource}
                renderingRootId={renderingRootId}
                renderItemContent={renderItemContent}
            />
        )
    }
)

export default ViewVariation;

function getStatusViewSubtitle(
    workspace: WorkspaceModelType,
    treeItem: DocumentItem,
    locale: string
): string | null {
    if (workspace.view !== 'trash' && workspace.view !== 'archive') {
        return null;
    }

    const item = treeItem.data.item;
    const libraryId = workspace.getStatusSourceLibraryIdForItem(item.id);
    const library = libraryId ? workspace.items.get(libraryId) : null;
    if (library && library.id !== item.id) {
        return library.getNameWithPlaceholder(locale);
    }

    const parent = treeItem.data.parentItemId
        ? workspace.items.get(treeItem.data.parentItemId)
        : null;
    if (parent && parent.id !== item.id) {
        return parent.getNameWithPlaceholder(locale);
    }

    return null;
}
